import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// PrimeReact Calendar
import { Calendar } from "primereact/calendar";

import Header from "../../Components/Header";
import Loader from "../../Components/Loader";
import NoDataFound from "../../Components/NoData";

import { useAlert } from "../../Modal/AlertProviderModal";
import { getData } from "../../Services/Util";
import { Language } from "../../Services/GlobalData";
import {
  fetchOutstandingInvoice,
  fetchPaymentLink,
} from "../../Services/AppAction";

import "../../assets/style/TransactionsStyles.scss";
import "../../assets/style/InvoicePayment.scss";

// ─── Interfaces ──────────────────────────────────────────────────────────────
export interface InvoiceItem {
  id: string;
  invoiceNo: string;
  type: string;
  date: string;
  status: string;
  totalAmount: number;
  outstandingAmount: number;
  advanceAmount: number;
  receivedAmount: number;
  paymentStatus: string;
  partyCode: string;
  partyName: string;
  custId: string;
  compId: string;
}

const InvoicePayment = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Dates state (default: past 30 days)
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Custom pay amount state
  const [customPayAmount, setCustomPayAmount] = useState<string>("");

  // ─── Initialize Dates ──────────────────────────────────────────────────────
  useEffect(() => {
    const today = new Date();
    const back30 = new Date();
    back30.setDate(today.getDate() - 30);
    setStartDate(back30);
    setEndDate(today);
  }, []);

  // Fetch whenever both dates change
  useEffect(() => {
    if (startDate && endDate) {
      loadInvoices();
    }
  }, [startDate, endDate]);

  // ─── Format Date to DD/MM/YYYY for API ─────────────────────────────────────
  const formatDateForApi = (date: Date): string => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // ─── Fetch Invoices from API ───────────────────────────────────────────────
  const loadInvoices = async () => {
    if (!startDate || !endDate) return;

    try {
      setLoading(true);
      const userData = await getData("userData");
      const custId = userData?.PARTY_ID || "";

      const startStr = formatDateForApi(startDate);
      const endStr = formatDateForApi(endDate);

      const res = await fetchOutstandingInvoice(custId, startStr, endStr);

      if (res?.status === 0 && Array.isArray(res.data)) {
        const mapped: InvoiceItem[] = res.data.map((item: any, idx: number) => {
          const outstanding = Number(
            item["OUTSTANDING AMT"] ?? item.OUTSTANDING_AMT ?? 0
          );
          const totalAmt = Number(item.AMOUNT ?? item.INVH_TOT_AMT ?? 0);
          const advanceAmt = Number(item["ADVANCE AMT"] ?? 0);
          const receivedAmt = Number(item.RECEIVED_AMT ?? 0);

          const displayStatus =
            outstanding > 0 ? "DUE" : item.PAYMENT_STATUS || "PAID";

          return {
            id: item.INVH_NO || `inv-${idx}`,
            invoiceNo: item.INVH_NO || "-",
            type: "Invoice",
            date: item.INVH_DATE || "",
            status: displayStatus,
            totalAmount: totalAmt,
            outstandingAmount: outstanding,
            advanceAmount: advanceAmt,
            receivedAmount: receivedAmt,
            paymentStatus: item.PAYMENT_STATUS || "",
            partyCode: item.PARTY_CODE || "",
            partyName: item.PARTY_NAME || "",
            custId: item.CUST_ID || "",
            compId: item.COMP_ID || "",
          };
        });

        setInvoices(mapped);
      } else {
        setInvoices([]);
        if (res?.message && res?.status !== 0) {
          showAlert("error", res.message);
        }
      }

      setSelectedIds([]);
      setCustomPayAmount("");
    } catch (error) {
      console.error("Error loading outstanding invoices:", error);
      showAlert("error", "Failed to load outstanding invoices.");
      setInvoices([]);
      setSelectedIds([]);
      setCustomPayAmount("");
    } finally {
      setLoading(false);
    }
  };

  // ─── Selection Logic ───────────────────────────────────────────────────────
  const isAllSelected =
    invoices.length > 0 && selectedIds.length === invoices.length;

  const selectedOutstanding = useMemo(() => {
    return invoices
      .filter((inv) => selectedIds.includes(inv.id))
      .reduce((sum, inv) => sum + Number(inv.outstandingAmount || 0), 0);
  }, [invoices, selectedIds]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
      setCustomPayAmount("");
    } else {
      const allIds = invoices.map((inv) => inv.id);
      setSelectedIds(allIds);
      const total = invoices.reduce(
        (s, i) => s + Number(i.outstandingAmount || 0),
        0
      );
      setCustomPayAmount(total > 0 ? String(total) : "");
    }
  };

  const handleToggleSelect = (id: string) => {
    const isSelected = selectedIds.includes(id);
    const updated = isSelected
      ? selectedIds.filter((item) => item !== id)
      : [...selectedIds, id];
    setSelectedIds(updated);

    const newTotal = invoices
      .filter((inv) => updated.includes(inv.id))
      .reduce((s, inv) => s + Number(inv.outstandingAmount || 0), 0);
    setCustomPayAmount(newTotal > 0 ? String(newTotal) : "");
  };

  // ─── Custom Amount Handler & Dynamic Math ──────────────────────────────────
  const numericPayAmount = Number(customPayAmount) || 0;
  const remainingBalance = Math.max(0, selectedOutstanding - numericPayAmount);

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      if (Number(val) > selectedOutstanding) {
        setCustomPayAmount(String(selectedOutstanding));
      } else {
        setCustomPayAmount(val);
      }
    }
  };

  const handleSetPayFull = () => {
    setCustomPayAmount(String(selectedOutstanding));
  };

  console.log("Track @ URL: ", `${process.env.REACT_APP_BASE_URL}transactions`)

  // ─── Payment Link Generation & Redirection ─────────────────────────────────
  const handleProceedToPay = async () => {
    if (selectedIds.length === 0 || numericPayAmount <= 0) {
      showAlert(
        "warning",
        "Please select at least one invoice and enter a valid amount."
      );
      return;
    }

    try {
      setLoading(true);
      const userData = await getData("userData");

      const selectedInvoices = invoices.filter((inv) =>
        selectedIds.includes(inv.id)
      );
      const invoiceSummary = selectedInvoices
        .map((i) => i.invoiceNo)
        .join(", ");
      const referenceId = `PAY_${Date.now()}_${userData?.PARTY_CODE || "REF"}`;

      const payload = {
        amount: numericPayAmount,
        currency: "INR",
        description: `Payment for Invoices: ${invoiceSummary}`.slice(0, 100),
        reference_id: referenceId,
        email: "susanta.dey@nihilent.com",
        phone: "8017055981",
        return_url: `${process.env.REACT_APP_REDIRECT_URL}transactions`,
        notify_customer: {
          email: true,
          sms: false,
        },
      };

      const res = await fetchPaymentLink(payload);

      // Extract url from: res.data.payment_links.url
      const paymentUrl =
        res?.data?.payment_links?.url ||
        res?.data?.url ||
        res?.payment_links?.url;

      if (res?.status === 0 && paymentUrl) {
        // Redirect the user to the Zoho Checkout Page
        window.location.href = paymentUrl;
      } else {
        showAlert("error", res?.message || "Failed to generate payment link.");
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
      showAlert(
        "error",
        "An error occurred while generating the payment link."
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Date Helpers ──────────────────────────────────────────────────────────
  const getMaxEndDate = (): Date => {
    if (!startDate) return new Date();
    const maxEnd = new Date(startDate);
    maxEnd.setDate(maxEnd.getDate() + 365);
    const today = new Date();
    return maxEnd < today ? maxEnd : today;
  };

  const handleStartDateChange = (date: Date | null) => {
    if (!date) return;
    if (endDate) {
      const diff =
        Math.abs(endDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
      if (diff > 365) {
        const adjusted = new Date(date);
        adjusted.setDate(adjusted.getDate() + 365);
        const today = new Date();
        setEndDate(adjusted > today ? today : adjusted);
      }
    }
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date) return;
    if (startDate) {
      const diff =
        Math.abs(date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      if (diff > 365) {
        showAlert("warning", "Date range cannot exceed 365 days.");
        return;
      }
    }
    setEndDate(date);
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const formatAmount = (amt: number | string) =>
    `₹ ${Number(amt || 0)?.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="invoice-pay-page">
      {/* ── Header ── */}
      <Header
        isShowBack
        navigate={navigate}
        goBack={() => navigate(-1)}
        title="Pay Invoices"
      />

      <div className="invoice-pay-content">
        {/* ── 1. Date Filters Row ── */}
        <div className="trx-date-row invoice-pay-date-row">
          <div className="trx-date-item">
            <span className="trx-date-label">Start Date</span>
            <div className="trx-calendar-wrap">
              <i className="pi pi-calendar trx-calendar-icon" />
              <Calendar
                value={startDate}
                onChange={(e) => handleStartDateChange(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder={Language.Lbl_StDate || "Start Date"}
                maxDate={new Date()}
                showIcon={false}
                readOnlyInput
                inputClassName="trx-calendar-input"
                panelClassName="trx-calendar-panel"
                className="trx-calendar"
              />
              <i className="pi pi-chevron-down trx-calendar-chevron" />
            </div>
          </div>

          <div className="trx-date-item">
            <span className="trx-date-label">End Date</span>
            <div className="trx-calendar-wrap">
              <i className="pi pi-calendar trx-calendar-icon" />
              <Calendar
                value={endDate}
                onChange={(e) => handleEndDateChange(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder={Language.Lbl_EndDate || "End Date"}
                maxDate={getMaxEndDate()}
                minDate={startDate ?? undefined}
                showIcon={false}
                readOnlyInput
                inputClassName="trx-calendar-input"
                panelClassName="trx-calendar-panel"
                className="trx-calendar"
              />
              <i className="pi pi-chevron-down trx-calendar-chevron" />
            </div>
          </div>
        </div>

        {/* ── 2. Standout "Select All" Banner Card ── */}
        {invoices.length > 0 && (
          <div
            className={`invoice-select-all-banner ${
              isAllSelected ? "invoice-select-all-banner--active" : ""
            }`}
            onClick={handleSelectAll}
          >
            <div className="select-all-left">
              <input
                type="checkbox"
                className="invoice-custom-checkbox banner-checkbox"
                checked={isAllSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
              />
              <div className="select-all-texts">
                <span className="select-all-title">Select All Invoices</span>
                <span className="select-all-subtitle">
                  Click to select all {invoices.length} outstanding invoices
                </span>
              </div>
            </div>

            <div className="select-all-right">
              <span className="select-all-badge">
                {selectedIds.length} of {invoices.length} Selected
              </span>
            </div>
          </div>
        )}

        {/* ── 3. Cards List ── */}
        <div className="invoice-pay-list">
          {invoices.length === 0 && !loading ? (
            <div className="invoice-pay-empty">
              <NoDataFound />
            </div>
          ) : (
            invoices.map((invoice) => {
              const isSelected = selectedIds.includes(invoice.id);

              return (
                <div key={invoice.id} className="invoice-row-item">
                  {/* Left Column Checkbox */}
                  <div className="invoice-row-checkbox-col">
                    <input
                      type="checkbox"
                      className="invoice-custom-checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelect(invoice.id)}
                    />
                  </div>

                  {/* Invoice Card */}
                  <div
                    className={`invoice-card ${
                      isSelected ? "invoice-card--selected" : ""
                    }`}
                    onClick={() => handleToggleSelect(invoice.id)}
                  >
                    {/* Left Amber Color Strip */}
                    <div className="invoice-card-strip" />

                    <div className="invoice-card-body">
                      {/* Top Row: Icon + ID/Type + Date + Badge */}
                      <div className="invoice-card-top">
                        <div className="invoice-card-top-left">
                          <div className="invoice-card-icon-wrap">
                            <i className="pi pi-file invoice-card-icon" />
                          </div>
                          <div className="invoice-card-id-col">
                            <span className="invoice-card-id">
                              {invoice.invoiceNo}
                            </span>
                            <span className="invoice-card-type">
                              {invoice.type}
                            </span>
                          </div>
                        </div>

                        <div className="invoice-card-top-right">
                          <span className="invoice-card-date">
                            {formatDateDisplay(invoice.date)}
                          </span>
                          <span className="invoice-card-badge">
                            {invoice.status}
                          </span>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="invoice-card-divider" />

                      {/* Bottom Row: Total Amount & Outstanding Amount */}
                      <div className="invoice-card-bottom">
                        <div className="invoice-card-stat">
                          <span className="invoice-card-stat-label">
                            Total Amount
                          </span>
                          <span className="invoice-card-stat-value invoice-card-stat-value--total">
                            {formatAmount(invoice.totalAmount)}
                          </span>
                        </div>

                        <div className="invoice-card-stat">
                          <span className="invoice-card-stat-label">
                            Outstanding
                          </span>
                          <span className="invoice-card-stat-value invoice-card-stat-value--outstanding">
                            {formatAmount(invoice.outstandingAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── 4. Sticky Bottom Footer with Editable Payment Amount ── */}
      <div className="invoice-pay-footer">
        {/* Top Summary & Dynamic Calculation Row */}
        <div className="footer-calc-row">
          <div className="footer-calc-item">
            <span className="calc-label">Selected Outstanding</span>
            <span className="calc-val">
              {formatAmount(selectedOutstanding)}
            </span>
          </div>

          <div className="footer-divider-v" />

          {/* Amount to Pay Input */}
          <div className="footer-input-item">
            <div className="input-label-row">
              <span className="calc-label">Pay Now Amount</span>
              {selectedOutstanding > 0 &&
                numericPayAmount < selectedOutstanding && (
                  <button
                    type="button"
                    className="pay-full-pill"
                    onClick={handleSetPayFull}
                  >
                    Pay Full
                  </button>
                )}
            </div>
            <div className="input-box-wrap">
              <span className="input-currency-prefix">₹</span>
              <input
                type="text"
                inputMode="decimal"
                className="pay-amount-input"
                placeholder="0"
                value={customPayAmount}
                onChange={handleCustomAmountChange}
                disabled={selectedIds.length === 0 || loading}
              />
            </div>
          </div>

          <div className="footer-divider-v" />

          <div className="footer-calc-item">
            <span className="calc-label">Remaining Balance</span>
            <span
              className={`calc-val ${
                remainingBalance > 0 ? "calc-val--orange" : "calc-val--green"
              }`}
            >
              {formatAmount(remainingBalance)}
            </span>
          </div>
        </div>

        {/* Action Row */}
        <div className="footer-action-row">
          <div className="footer-selection-summary">
            Selected: <strong>{selectedIds.length}</strong>{" "}
            {selectedIds.length === 1 ? "invoice" : "invoices"}
          </div>

          <button
            className={`invoice-pay-btn ${
              selectedIds.length === 0 || numericPayAmount <= 0 || loading
                ? "invoice-pay-btn--disabled"
                : ""
            }`}
            disabled={
              selectedIds.length === 0 || numericPayAmount <= 0 || loading
            }
            onClick={handleProceedToPay}
          >
            {loading
              ? "Processing..."
              : `Pay ${formatAmount(numericPayAmount)}`}
          </button>
        </div>
      </div>

      {loading && <Loader loading={loading} />}
    </div>
  );
};

export default InvoicePayment;
